// Use-case buckets are the navigation primitive layered on top of modes.
// Single-home rule: every mode lives in exactly one bucket (see modesRegistry).
//
// Active bucket is derived from the current mode — there is no separate bucket
// state. Switching buckets is implemented as `setMode(defaultModeFor(bucket))`.

import type { AuthContextValue } from "@/contexts/AuthContext";
import {
  canAccessPrimaryMode,
  type PrimaryMode,
} from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { AnchorEntitlements } from "@/lib/entitlements/anchorsClient";
import { canAccessByAnchor } from "@/lib/entitlements/modeAnchorRequirement";

export type BucketId =
  | "sales"
  | "dealer"
  | "manager"
  | "admin"
  | "platform"
  | "manufacturing"
  | "production"
  | "fulfillment"
  | "service"
  | "ops"
  | "logistics"
  | "coverage";

export type BucketRegistryEntry = {
  id: BucketId;
  label: string;
  /** Single-letter glyph for the icon-only collapsed rail / mobile drawer affordance. */
  glyph: string;
  /** Modes that live in this bucket, ordered. First accessible entry is the default. */
  modeIds: PrimaryMode[];
};

// Bucket-rail order is a product decision, not a code decision. The
// owner-mandated sequence (dev-sync note 81ff9e9d, 2026-06-15) is:
//
//   Sales → Dealer → Fulfillment → Service → Manager → Logistics → Ops →
//     Production → Manufacturing → Admin → Platform
//
// `dealer` (OI-5b Q1, 2026-07-09) is the slim subscriber portal — Place
// Order + Order History only — for turbo_sales orgs that are not also
// manufacturers. Rationale: sits immediately after Sales so dealer-org
// users find the order surface without the full Sales multi-mode shell.
export const BUCKETS_REGISTRY: BucketRegistryEntry[] = [
  {
    id: "sales",
    label: "Sales",
    glyph: "S",
    // `salesIntake` sits between `leads` and `salesConsole` per the
    // Order Intake bucket move (docs/products/turbo_sales.md §6). The
    // landing rule means a sales-rep still defaults to `leads` (their
    // daily-driver capture surface); a Mfg-only user lacking `leads`
    // access falls through to `salesIntake`, which renders the
    // Mfg-accepter variant — same content they saw at the old
    // `mfgOrderIntake` home before the move.
    // `opportunities` sits between `salesIntake` and `salesConsole`. The
    // Opportunity Engine (Phase 1, 2026-06-15 per docs/GPT_To_Repo.md) is
    // the reusable opportunity / pricing / documents / generated-PDF
    // workflow that replaces the fillable PDF Red Iron process. Placed
    // after `salesIntake` so the "submit captured lead as deal" flow
    // (`salesIntake.sales_rep` variant) still flows naturally into the
    // formal opportunity workspace next to it; placed before
    // `salesConsole` so a rep who lands on `leads` (the default) can
    // shift right into the deal-writing surfaces before the
    // conversation-context tools.
    modeIds: ["leads", "salesIntake", "opportunities", "salesConsole", "adsAssistant", "training"],
  },
  {
    // OI-5b / Q1 — slim subscriber-dealer portal. Visible only when the
    // org has turbo_sales and not turbo_manufacturing (anchor gate on
    // the modes). Role gate: SALES_REP_ROLES.
    id: "dealer",
    label: "Dealer",
    glyph: "D",
    modeIds: ["dealerPlaceOrder", "dealerOrderHistory"],
  },
  {
    // Fulfillment is the lot manager / receiver / intake-form surface
    // -- the home for non-production inventory entry (cash purchases /
    // repos), the three-tier inspection-intake form lifecycle, and (in
    // future slices) sales-side orders + service-request origination.
    // See docs/planning/sales_fulfillment_bucket.md for the full scope.
    //
    // Baseline bucket (no entitlement key) gated by the per-org
    // capability column `organizations.has_sales_floor` (default true
    // for retail-flavored orgs; off for plant-only mfg orgs). The
    // Slice-1 VITE_FULFILLMENT_PILOT scaffold flag retired in Slice 3.
    //
    // Glyph "U" is for fUlfillment -- avoids the F-collision with
    // Manufacturing-Factory, the L-collision with Logistics, and the
    // O-collision-with-Orders that the planning doc §2.2 surfaced.
    id: "fulfillment",
    label: "Fulfillment",
    glyph: "U",
    // Order matters: getDefaultModeForBucket returns the first
    // accessible entry, so Inventory stays the bucket landing page
    // (planning doc §4 table row 1 -- "what a lot manager opens
    // first"). Intake forms sit in the middle as the queue surface;
    // New inventory (the bulk / single-piece entry form) is last
    // because it's the low-frequency / high-density operation.
    modeIds: ["fulfillmentInventory", "fulfillmentOrders", "fulfillmentIntake", "fulfillmentForms", "fulfillmentEntry"],
  },
  {
    // Turbo Service — field-service scheduling + inbound repair/upgrade
    // requests. Embedded layer (any anchor); operational modes gated by
    // `organizations.services_enabled` (default false). See
    // docs/planning/service_bucket.md §5.4.
    id: "service",
    label: "Service",
    glyph: "V",
    // Order matters: `getDefaultModeForBucket` returns the first accessible
    // entry, so Service setup (`serviceDefinitions`) is the admin landing
    // page — configure offerings, quoting, and fleet before dispatch surfaces.
    modeIds: [
      "serviceDefinitions",
      "serviceBoard",
      "serviceRequests",
      "serviceScheduling",
      "serviceMaterialsUsage",
    ],
  },
  {
    id: "manager",
    label: "Manager",
    glyph: "M",
    // Order matters: `getDefaultModeForBucket` returns the first accessible
    // entry, so the bucket's homepage is whatever sits at index 0.
    // Documents Editor is the manager landing page (dev-sync 229ece14).
    // AI Prompting is hidden from nav pending rethink (dev-sync 87e7a514);
    // the mode remains registered for deep links / platform tooling.
    // Training Authoring + Ads Guidance sit next to Knowledge Base since the
    // three surfaces share the trainer/marketing workflow (KB suggest is wired
    // into the scenario editor; ads guidance is the same trainer-facing role).
    // Both were re-homed out of the legacy `admin` bucket / Sales bucket
    // per docs/design/admin_split_and_entitlements.md §7.1 + §7.6.
    modeIds: [
      "formsAndFormats",
      "locationTools",
      "knowledgebase",
      "trainingAuthor",
      "adsGuidance",
      "audienceData",
    ],
  },
  {
    id: "coverage",
    label: "Coverage",
    glyph: "C",
    modeIds: ["coverageAssistant"],
  },
  {
    // Logistics is the "how does a finished unit get to its customer" surface:
    // routes (per-unit dispatch state machines), stops (ordered legs), and
    // carriers (the haulers we book). Backend tables live under the logi_*
    // prefix (logi_routes / logi_stops / logi_carriers / logi_carrier_rigs)
    // after the mfg_* → logi_* rename; entitlement still rides the shared
    // manufacturing_suite key until the per-bucket logistics carve-out
    // lands. Its own bucket so it can grow (carriers tab, stops timeline,
    // scheduling board, AI route opt) without exploding the manufacturing
    // bucket.
    id: "logistics",
    label: "Logistics",
    glyph: "L",
    // Order matters: getDefaultModeForBucket returns the first accessible
    // entry, so Routes stays the bucket landing page. Carriers is the
    // admin-y sibling (manage haulers; the routes picker pulls is_active
    // carriers from this same backend).
    modeIds: ["logisticsRoutes", "logisticsCalendar", "logisticsCarriers"],
  },
  {
    // Ops — promoted out of the legacy `admin` bucket per
    // docs/design/admin_split_and_entitlements.md §3 + §6.2. The
    // partner slice to the Admin split: required for the layer-rendering
    // contract in the handoff §4 to be literally true rather than
    // aspirational. Hosts the `operations` mode (`OpsPaperworkPanel`)
    // today; the spec's §3 table lists deal paperwork pulse,
    // deal-as-recipe authoring, RTO terms config (pending a separate
    // re-home slice), FastField template authoring + ledger,
    // integration ingest dashboards, and comm oversight as the future
    // surfaces that grow here. No separate v1 entitlement key — the
    // bucket renders inside whichever anchor (Turbo Sales /
    // Turbo Manufacturing) the customer subscribes to; the gate is
    // "at least one anchor entitlement" via the existing per-mode
    // `TAB_ACCESS` checks. Glyph "O" since the other letters are
    // taken. Position in the rail (between Logistics and Production)
    // follows the owner-mandated sequence in the file-level header,
    // superseding the prior "data-flow" rationale that originally
    // placed Ops between Fulfillment and Logistics.
    id: "ops",
    label: "Ops",
    glyph: "O",
    // Order matters: `getDefaultModeForBucket` returns the first accessible
    // entry. `operations` (label **Deals**) stays the bucket home — live
    // deal instances. `dealTypes` authors archetypes. `rtoTerms` carved out
    // of Tenant Admin → Org Administration on 2026-06-15 per
    // docs/design/admin_split_and_entitlements.md §7.7.
    modeIds: ["operations", "dealTypes", "opsLookup", "opsObligations", "rtoTerms", "dailyChecklist", "physicalInventory"],
  },
  {
    // Production is the floor / efficiency view of manufacturing -- the
    // planned home of the build pipeline kanban, station-level build
    // tracking, materials consumption events (per-station as-built),
    // labor time-tracking, throughput / cycle-time dashboards, and the
    // BOM-vs-actual reconciliation surface. Currently empty of real
    // surfaces: only the `mfgFulfill` scaffold lives here (gated by
    // VITE_MFG_SCAFFOLD_TABS) until the first Production-bucket feature
    // slice graduates the build kanban into a real landing mode.
    //
    // Split out of Manufacturing per
    // docs/design/buckets_and_entitlements.md §2 (read for the full
    // seam reasoning + the friendly-competitor wedge that drove it).
    // Glyph "B" is for Build (the act of producing) since "P" is taken
    // by Platform; Build is also the alternate name surfaced in the
    // bucket-doc decision log.
    id: "production",
    label: "Production",
    glyph: "B",
    modeIds: ["mfgFulfill", "mfgAudits", "mfgInventoryCount", "mfgPurchasing", "mfgLaborPay", "mfgBuilderAssistant", "mfgQcDashboard"],
  },
  {
    // Manufacturing is the desk / management view of manufacturing:
    // materials, COGS, ERP, models (BOM), plants, units history, and
    // (future) reorder prediction + financial reports. Sister bucket
    // is Production (the floor / efficiency view, split out per
    // docs/design/buckets_and_entitlements.md §2). Both buckets share
    // the same `mfg_*` backend tables and the manufacturing role gate
    // for v1; the split is purely UI + future per-side sub-roles.
    id: "manufacturing",
    label: "Manufacturing",
    glyph: "F",
    // Order matters: `getDefaultModeForBucket` returns the first accessible
    // entry, so Inventory is the bucket's home page. Scaffold modes
    // (Reports / Admin Home) sit at the end and are hidden in pilot via
    // `canAccessPrimaryMode` until VITE_MFG_SCAFFOLD_TABS is set.
    // `mfgInventory` hosts the merged Inventory + Receive screen as of
    // the 2026-06-02 merge slice (the receive-lines + recipe-count form
    // moved into the right column of the inventory panel; `mfgReceive`
    // mode + ManufacturingReceivePanel are deleted).
    modeIds: [
      "mfgInventory",
      "mfgModels",
      "mfgPricing",
      "mfgOverhead",
      // The pre-2026-06-15 `mfgOrderIntake` mode moved out of this
      // bucket as part of the Order Intake bucket move
      // (docs/products/turbo_sales.md §5). The Mfg-role variant of the
      // new Sales-bucket `salesIntake` mode renders the same content.
      "mfgOrders",
      "mfgPlants",
      // Build pipeline owns Plant Kanban + Units roster (+ phase settings).
      // Former top-level mfgUnits mode was folded into the Units sub-tab.
      // Vendor directory lives in Production → Purchasing (Vendors tab).
      "mfgBuildPipeline",
      "mfgLaborBilling",
      "mfgReports",
      "mfgAdminHome",
    ],
  },
  {
    // Admin — the now-narrowed platform-substrate bucket per
    // docs/design/admin_split_and_entitlements.md §3 + §6.2. Hosts the
    // anchor-independent self-service surfaces: App User Admin, Contacts
    // Directory, Org Settings, Locations, Org lifecycle. Bucket id stays
    // `admin` for stability; user-facing label is "Admin" (was briefly
    // "Tenant Admin" during the admin-split landing — reverted on
    // 2026-06-16 since the narrowing is intuitive without the "Tenant"
    // qualifier and the longer label crowded the bucket rail). The
    // Operations mode promoted out to its own `ops` bucket; Training
    // authoring + Ads guidance authoring moved to Manager bucket; RTO
    // Settings is pending the Ops re-home of the RTO sub-section.
    id: "admin",
    label: "Admin",
    glyph: "A",
    modeIds: ["admin", "dashboard"],
  },
  {
    id: "platform",
    label: "Platform",
    glyph: "P",
    modeIds: ["dev", "platform"],
  },
];

const BUCKET_BY_ID: Record<BucketId, BucketRegistryEntry> = BUCKETS_REGISTRY.reduce(
  (acc, b) => {
    acc[b.id] = b;
    return acc;
  },
  {} as Record<BucketId, BucketRegistryEntry>
);

/** Reverse lookup: which bucket does this mode belong to? */
export const MODE_TO_BUCKET: Record<PrimaryMode, BucketId> = (() => {
  const map = {} as Record<PrimaryMode, BucketId>;
  for (const bucket of BUCKETS_REGISTRY) {
    for (const modeId of bucket.modeIds) {
      if (map[modeId]) {
        throw new Error(
          `bucketsRegistry: mode "${modeId}" assigned to multiple buckets (single-home rule violated).`
        );
      }
      map[modeId] = bucket.id;
    }
  }
  return map;
})();

export function getBucketById(id: BucketId): BucketRegistryEntry {
  return BUCKET_BY_ID[id];
}

export function getBucketForMode(mode: PrimaryMode): BucketId | null {
  return MODE_TO_BUCKET[mode] ?? null;
}

function passesBothGates(
  modeId: PrimaryMode,
  auth: AuthContextValue,
  anchors: AnchorEntitlements | null | undefined
): boolean {
  if (!canAccessPrimaryMode(modeId, auth)) return false;
  return canAccessByAnchor(modeId, anchors ?? null);
}

/**
 * Buckets where the user has access to at least one mode under the composed
 * role + anchor-entitlement gate. The `anchors` arg is optional and defaults
 * to "loading" semantics (open) per `canAccessByAnchor` — i.e. passing
 * `undefined` returns the role-only filter, matching the pre-2026-06-15
 * behavior. Callers that have access to `useAnchorEntitlements()` should
 * pass the loaded anchor set so embedded-layer + anchor-specific buckets
 * fold out of the rail for orgs that lack the entitlement.
 */
export function getAccessibleBuckets(
  auth: AuthContextValue,
  anchors?: AnchorEntitlements | null
): BucketRegistryEntry[] {
  return BUCKETS_REGISTRY.filter((bucket) =>
    bucket.modeIds.some((modeId) => passesBothGates(modeId, auth, anchors))
  );
}

/**
 * The first accessible mode inside `bucketId`, in registry order. Returns null
 * if the user has no accessible mode in the bucket (shouldn't happen when the
 * bucket itself was filtered through `getAccessibleBuckets`).
 */
export function getDefaultModeForBucket(
  bucketId: BucketId,
  auth: AuthContextValue,
  anchors?: AnchorEntitlements | null
): PrimaryMode | null {
  const bucket = BUCKET_BY_ID[bucketId];
  if (!bucket) return null;
  for (const modeId of bucket.modeIds) {
    if (passesBothGates(modeId, auth, anchors)) return modeId;
  }
  return null;
}

/**
 * Resolves the bucket to render as "active". Prefers the bucket the current
 * mode belongs to when that bucket still has an accessible mode; otherwise
 * falls through to the first accessible bucket. Needed when impersonation or
 * a role change leaves the persisted mode in a bucket the user can no longer
 * open (purchasing-only + leftover Sales/`leads` was a blank, unswitchable rail).
 */
export function resolveActiveBucket(
  mode: PrimaryMode,
  auth: AuthContextValue,
  anchors?: AnchorEntitlements | null
): BucketId | null {
  const fromMode = getBucketForMode(mode);
  if (fromMode && getDefaultModeForBucket(fromMode, auth, anchors)) return fromMode;
  const accessible = getAccessibleBuckets(auth, anchors);
  return accessible[0]?.id ?? null;
}
